/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  OperationStatus,
  OperationType,
  StandbyActivationType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OperationProps {
  id: string;
  status: OperationStatus;
  type: OperationType;
  unitId: string | null;
  vehicleId: string;
  dateOperationStarted: Date;
  dateOperationEnded: Date | null;
  unitActivationTime: Date | null;
  riskAnalysisNumber: string | null;
  standbyActivationType: StandbyActivationType | null;
  caller: string | null;
  callbackNumber: string | null;
  initialOperationType: OperationType | null;
  riskAnalysisAcknowledged: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OperationRecord extends Record<OperationProps>({
  id: null as any,
  status: null as any,
  type: null as any,
  unitId: null as any,
  vehicleId: null as any,
  dateOperationStarted: null as any,
  dateOperationEnded: null as any,
  unitActivationTime: null as any,
  riskAnalysisNumber: null as any,
  standbyActivationType: null as any,
  caller: null as any,
  callbackNumber: null as any,
  initialOperationType: null as any,
  riskAnalysisAcknowledged: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: OperationStatus;
    type: OperationType;
    unitId: string | null;
    vehicleId: string;
    dateOperationStarted: string | number | Date;
    dateOperationEnded: string | number | Date | null;
    unitActivationTime: string | number | Date | null;
    riskAnalysisNumber: string | null;
    standbyActivationType: StandbyActivationType | null;
    caller: string | null;
    callbackNumber: string | null;
    initialOperationType: OperationType | null;
    riskAnalysisAcknowledged: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OperationRecord {
    if (json == null) {
      throw new Error('OperationRecord.fromJS: parameter cannot be null');
    }
    return new OperationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OperationJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', OperationStatus.CrewAccepted),
        'OperationJson.status',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', OperationType.SceneCall),
        'OperationJson.type',
      ),
      unitId: json.unitId,
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'OperationJson.vehicleId',
      ),
      dateOperationStarted: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateOperationStarted', '0001-01-01T00:00:00.00Z'),
          'OperationJson.dateOperationStarted',
        ),
      ),
      dateOperationEnded:
        json.dateOperationEnded != null ? new Date(json.dateOperationEnded) : null,
      unitActivationTime:
        json.unitActivationTime != null ? new Date(json.unitActivationTime) : null,
      riskAnalysisNumber: json.riskAnalysisNumber,
      standbyActivationType: json.standbyActivationType,
      caller: json.caller,
      callbackNumber: json.callbackNumber,
      initialOperationType: json.initialOperationType,
      riskAnalysisAcknowledged: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'riskAnalysisAcknowledged', false),
        'OperationJson.riskAnalysisAcknowledged',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'OperationJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: OperationStatus;
    type: OperationType;
    unitId: string | null;
    vehicleId: string;
    dateOperationStarted: string | number | Date;
    dateOperationEnded: string | number | Date | null;
    unitActivationTime: string | number | Date | null;
    riskAnalysisNumber: string | null;
    standbyActivationType: StandbyActivationType | null;
    caller: string | null;
    callbackNumber: string | null;
    initialOperationType: OperationType | null;
    riskAnalysisAcknowledged: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OperationRecord, Error[]] {
    if (json == null) {
      throw new Error('OperationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OperationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OperationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', OperationStatus.CrewAccepted),
            'OperationJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', OperationType.SceneCall),
            'OperationJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'OperationJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOperationStarted: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateOperationStarted', '0001-01-01T00:00:00.00Z'),
              'OperationJson.dateOperationStarted',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOperationEnded: tryOrDefault(
        () => (json.dateOperationEnded != null ? new Date(json.dateOperationEnded) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitActivationTime: tryOrDefault(
        () => (json.unitActivationTime != null ? new Date(json.unitActivationTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      riskAnalysisNumber: tryOrDefault(
        () => json.riskAnalysisNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      standbyActivationType: tryOrDefault(
        () => json.standbyActivationType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      caller: tryOrDefault(
        () => json.caller,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      callbackNumber: tryOrDefault(
        () => json.callbackNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialOperationType: tryOrDefault(
        () => json.initialOperationType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      riskAnalysisAcknowledged: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'riskAnalysisAcknowledged', false),
            'OperationJson.riskAnalysisAcknowledged',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'OperationJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OperationProps) {
    super(props);
    if (props == null) {
      throw new Error('OperationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OperationViewModelBase implements OperationProps {
  protected readonly _props: OperationProps;
  private _json?: OperationProps;

  get id(): string {
    return this._props.id;
  }
  get status(): OperationStatus {
    return this._props.status;
  }
  get type(): OperationType {
    return this._props.type;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get dateOperationStarted(): Date {
    return this._props.dateOperationStarted;
  }
  get dateOperationEnded(): Date | null {
    return this._props.dateOperationEnded;
  }
  get unitActivationTime(): Date | null {
    return this._props.unitActivationTime;
  }
  get riskAnalysisNumber(): string | null {
    return this._props.riskAnalysisNumber;
  }
  get standbyActivationType(): StandbyActivationType | null {
    return this._props.standbyActivationType;
  }
  get caller(): string | null {
    return this._props.caller;
  }
  get callbackNumber(): string | null {
    return this._props.callbackNumber;
  }
  get initialOperationType(): OperationType | null {
    return this._props.initialOperationType;
  }
  get riskAnalysisAcknowledged(): boolean {
    return this._props.riskAnalysisAcknowledged;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAborted() {
    return this._props.status === OperationStatus.Aborted;
  }
  public get isCancelled() {
    return this._props.status === OperationStatus.Cancelled;
  }
  public get isCompleted() {
    return this._props.status === OperationStatus.Completed;
  }
  public get isCrewAccepted() {
    return this._props.status === OperationStatus.CrewAccepted;
  }
  public get isOnStandby() {
    return this._props.status === OperationStatus.OnStandby;
  }
  public get isScheduled() {
    return this._props.status === OperationStatus.Scheduled;
  }
  public get isUnderway() {
    return this._props.status === OperationStatus.Underway;
  }
  public get isUnitActivated() {
    return this._props.status === OperationStatus.UnitActivated;
  }
  public get isTypeGroundRun() {
    return this._props.type === OperationType.GroundRun;
  }
  public get isInitialOperationTypeGroundRun() {
    return this._props.initialOperationType === OperationType.GroundRun;
  }
  public get isTypeMaintenance() {
    return this._props.type === OperationType.Maintenance;
  }
  public get isInitialOperationTypeMaintenance() {
    return this._props.initialOperationType === OperationType.Maintenance;
  }
  public get isTypeNonPatientShuttle() {
    return this._props.type === OperationType.NonPatientShuttle;
  }
  public get isInitialOperationTypeNonPatientShuttle() {
    return this._props.initialOperationType === OperationType.NonPatientShuttle;
  }
  public get isTypePatientShuttle() {
    return this._props.type === OperationType.PatientShuttle;
  }
  public get isInitialOperationTypePatientShuttle() {
    return this._props.initialOperationType === OperationType.PatientShuttle;
  }
  public get isTypePublicRelations() {
    return this._props.type === OperationType.PublicRelations;
  }
  public get isInitialOperationTypePublicRelations() {
    return this._props.initialOperationType === OperationType.PublicRelations;
  }
  public get isTypePublicUtility() {
    return this._props.type === OperationType.PublicUtility;
  }
  public get isInitialOperationTypePublicUtility() {
    return this._props.initialOperationType === OperationType.PublicUtility;
  }
  public get isTypeReposition() {
    return this._props.type === OperationType.Reposition;
  }
  public get isInitialOperationTypeReposition() {
    return this._props.initialOperationType === OperationType.Reposition;
  }
  public get isTypeResidence() {
    return this._props.type === OperationType.Residence;
  }
  public get isInitialOperationTypeResidence() {
    return this._props.initialOperationType === OperationType.Residence;
  }
  public get isTypeSceneCall() {
    return this._props.type === OperationType.SceneCall;
  }
  public get isInitialOperationTypeSceneCall() {
    return this._props.initialOperationType === OperationType.SceneCall;
  }
  public get isTypeSearchAndAssist() {
    return this._props.type === OperationType.SearchAndAssist;
  }
  public get isInitialOperationTypeSearchAndAssist() {
    return this._props.initialOperationType === OperationType.SearchAndAssist;
  }
  public get isTypeTraining() {
    return this._props.type === OperationType.Training;
  }
  public get isInitialOperationTypeTraining() {
    return this._props.initialOperationType === OperationType.Training;
  }
  public get isTypeTransfer() {
    return this._props.type === OperationType.Transfer;
  }
  public get isInitialOperationTypeTransfer() {
    return this._props.initialOperationType === OperationType.Transfer;
  }
  public get isTypeTransferCenter() {
    return this._props.type === OperationType.TransferCenter;
  }
  public get isInitialOperationTypeTransferCenter() {
    return this._props.initialOperationType === OperationType.TransferCenter;
  }
  public get isAutoLaunch() {
    return this._props.standbyActivationType === StandbyActivationType.AutoLaunch;
  }
  public get isCustomerActivation() {
    return this._props.standbyActivationType === StandbyActivationType.CustomerActivation;
  }

  public isAnyStatus(...operationStatus: OperationStatus[]) {
    return operationStatus.includes(this._props.status);
  }
  public isAnyType(...operationType: OperationType[]) {
    return operationType.includes(this._props.type);
  }
  public isAnyStandbyActivationType(...standbyActivationType: StandbyActivationType[]) {
    return (
      this._props.standbyActivationType != null &&
      standbyActivationType.includes(this._props.standbyActivationType)
    );
  }
  public isAnyInitialOperationType(...operationType: OperationType[]) {
    return (
      this._props.initialOperationType != null &&
      operationType.includes(this._props.initialOperationType)
    );
  }

  constructor(props: OperationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        type: this._props.type,
        unitId: this._props.unitId,
        vehicleId: this._props.vehicleId,
        dateOperationStarted: this._props.dateOperationStarted,
        dateOperationEnded: this._props.dateOperationEnded,
        unitActivationTime: this._props.unitActivationTime,
        riskAnalysisNumber: this._props.riskAnalysisNumber,
        standbyActivationType: this._props.standbyActivationType,
        caller: this._props.caller,
        callbackNumber: this._props.callbackNumber,
        initialOperationType: this._props.initialOperationType,
        riskAnalysisAcknowledged: this._props.riskAnalysisAcknowledged,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOperationEntities = (state: AppState) =>
  state.entityStores.operation.entities;

export const selectOperationById = (id: string) => (state: AppState) =>
  state.entityStores.operation.entities.get(id);

export const selectOperationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.operation.meta.get(id);

export const selectOperationAndMetadataById = (id: string) =>
  createSelector(selectOperationById(id), selectOperationMetadataById(id), (operation, meta) => ({
    operation,
    meta,
  }));

export function selectOperationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOperationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OperationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOperationEntitiesByStatus(status: OperationStatus) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectOperationEntitiesByType(type: OperationType) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectOperationEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectOperationEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectOperationEntitiesByDateOperationStarted(dateOperationStarted: Date) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.dateOperationStarted === dateOperationStarted).valueSeq(),
  );
}

export function selectOperationEntitiesByDateOperationEnded(dateOperationEnded: Date) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.dateOperationEnded === dateOperationEnded).valueSeq(),
  );
}

export function selectOperationEntitiesByUnitActivationTime(unitActivationTime: Date) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.unitActivationTime === unitActivationTime).valueSeq(),
  );
}

export function selectOperationEntitiesByRiskAnalysisNumber(riskAnalysisNumber: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.riskAnalysisNumber === riskAnalysisNumber).valueSeq(),
  );
}

export function selectOperationEntitiesByStandbyActivationType(
  standbyActivationType: StandbyActivationType,
) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.standbyActivationType === standbyActivationType).valueSeq(),
  );
}

export function selectOperationEntitiesByCaller(caller: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.caller === caller).valueSeq(),
  );
}

export function selectOperationEntitiesByCallbackNumber(callbackNumber: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.callbackNumber === callbackNumber).valueSeq(),
  );
}

export function selectOperationEntitiesByInitialOperationType(initialOperationType: OperationType) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.initialOperationType === initialOperationType).valueSeq(),
  );
}

export function selectOperationEntitiesByRiskAnalysisAcknowledged(
  riskAnalysisAcknowledged: boolean,
) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.riskAnalysisAcknowledged === riskAnalysisAcknowledged).valueSeq(),
  );
}

export function selectOperationEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllOperationEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
